import React, { useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileSpreadsheet, Loader2, CheckCircle2, ShieldAlert, Trash2,
} from "lucide-react";
import { importApi } from "../../lib/api";
import { TID } from "../../constants/testIds";
import { getApiError } from "../../lib/utils";
import { Stat } from "./Stat";
import { MassReplaceConfirmModal } from "./MassReplaceConfirmModal";

/** SUPER_ADMIN-only mass replacement. Cross-reference (safer) or
 *  Ignore-and-overwrite (destructive). Requires typing RESET to execute. */
export const MassReplaceTab = ({ onExecuted }) => {
  const replaceRef = useRef(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacePreview, setReplacePreview] = useState(null);
  const [replaceMode, setReplaceMode] = useState("cross_reference");
  const [deleteMissing, setDeleteMissing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [replaceReport, setReplaceReport] = useState(null);

  const previewReplace = async () => {
    if (!replaceFile) return;
    setPreviewing(true);
    setReplacePreview(null);
    setReplaceReport(null);
    try {
      const r = await importApi.previewReplace(replaceFile);
      setReplacePreview(r);
    } catch (e) {
      toast.error(getApiError(e, "Preview failed"));
    } finally { setPreviewing(false); }
  };

  const executeReplace = async () => {
    try {
      const r = await importApi.executeReplace(replaceFile, {
        mode: replaceMode,
        confirm: "RESET",
        deleteMissing: replaceMode === "cross_reference" ? deleteMissing : false,
      });
      setReplaceReport(r);
      setShowConfirm(false);
      setReplaceFile(null);
      setReplacePreview(null);
      toast.success(`Mass replacement complete (${r.mode})`);
      onExecuted?.();
    } catch (e) {
      toast.error(getApiError(e, "Replacement failed"));
      throw e;  // let the confirm modal know
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border-2 border-red-300 bg-red-50 p-4 text-xs text-red-900">
        <div className="flex items-start gap-2">
          <ShieldAlert size={18} className="mt-0.5 shrink-0 text-red-700" />
          <div>
            <div className="font-heading text-sm font-bold text-red-800 mb-1">
              High-consequence action — Super Admin only
            </div>
            <p>
              Mass replacement resets the facilities collection using a cleaned workbook. This can
              remove or overwrite hundreds of records. Always <b>preview first</b>. A snapshot backup of the
              current facilities is automatically saved to a timestamped collection before any change is
              applied so the operation can be reversed by database restore.
            </p>
            <p className="mt-1">
              <b>Audit logs, notifications and users are not affected.</b>
            </p>
          </div>
        </div>
      </div>

      <div
        data-testid={TID.massReplaceDropzone}
        onClick={() => replaceRef.current?.click()}
        className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-red-300 bg-white p-10 text-center hover:bg-red-50/40"
      >
        <input
          data-testid={TID.massReplaceFileInput}
          ref={replaceRef}
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
          onChange={(e) => {
            setReplaceFile(e.target.files?.[0] || null);
            setReplacePreview(null);
            setReplaceReport(null);
          }}
        />
        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-red-50 border border-red-200">
          <FileSpreadsheet size={22} className="text-red-700" strokeWidth={2} />
        </div>
        <div className="mt-3 font-heading text-lg font-bold text-slate-900">
          {replaceFile ? replaceFile.name : "Drop cleaned facilities workbook (.xlsx)"}
        </div>
        <div className="mt-1 text-xs text-slate-500">
          Use the same template as quarterly refresh · only .xlsx / .xlsm accepted
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        {replaceFile && (
          <button
            onClick={() => { setReplaceFile(null); setReplacePreview(null); setReplaceReport(null); }}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Clear
          </button>
        )}
        <button
          data-testid={TID.massReplacePreview}
          onClick={previewReplace}
          disabled={!replaceFile || previewing}
          className="inline-flex items-center gap-2 rounded-md border border-red-600 bg-white px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
        >
          {previewing ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} strokeWidth={2.5} />}
          Preview changes (dry-run)
        </button>
      </div>

      {replacePreview && (
        <div className="mt-4 space-y-4 rounded-md border border-slate-200 bg-white p-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Diff summary</div>
            <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-5">
              <Stat label="In file" value={replacePreview.counts.new_file_rows} />
              <Stat label="Currently in DB" value={replacePreview.counts.existing_db_rows} />
              <Stat label="Will add" value={replacePreview.counts.adds} tone="good" />
              <Stat label="Will update" value={replacePreview.counts.updates} tone="info" />
              <Stat label="Not in file" value={replacePreview.counts.removes} tone="warn" />
            </div>
          </div>

          <details className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
            <summary className="cursor-pointer font-semibold text-slate-800">
              Facilities in DB missing from new file ({replacePreview.counts.removes})
            </summary>
            <ul className="mt-2 max-h-64 overflow-y-auto space-y-1 pl-4 list-disc">
              {replacePreview.removes.map((r) => (
                <li key={r.facility_id}>
                  <span className="font-semibold">{r.name}</span>
                  <span className="ml-2 text-slate-500">· {r.category || "—"} · {r.region || "—"} · {r.status}</span>
                </li>
              ))}
              {replacePreview.counts.removes > replacePreview.removes.length && (
                <li className="text-slate-500 italic">...and {replacePreview.counts.removes - replacePreview.removes.length} more</li>
              )}
            </ul>
          </details>

          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Mode</div>
            <label
              data-testid={TID.massReplaceMode("cross_reference")}
              className={`flex cursor-pointer items-start gap-2 rounded-md p-2 ${replaceMode === "cross_reference" ? "bg-nmi-50 ring-1 ring-nmi-300" : "hover:bg-slate-50"}`}
            >
              <input
                type="radio"
                name="replace-mode"
                className="mt-1"
                checked={replaceMode === "cross_reference"}
                onChange={() => setReplaceMode("cross_reference")}
              />
              <div>
                <div className="text-sm font-bold text-slate-900">Cross-reference (safer)</div>
                <div className="text-xs text-slate-600">
                  Update matching rows, add new ones. Keeps current status &amp; audit history for facilities that still exist.
                </div>
              </div>
            </label>
            <label
              data-testid={TID.massReplaceMode("overwrite")}
              className={`mt-2 flex cursor-pointer items-start gap-2 rounded-md p-2 ${replaceMode === "overwrite" ? "bg-red-50 ring-1 ring-red-300" : "hover:bg-slate-50"}`}
            >
              <input
                type="radio"
                name="replace-mode"
                className="mt-1"
                checked={replaceMode === "overwrite"}
                onChange={() => setReplaceMode("overwrite")}
              />
              <div>
                <div className="text-sm font-bold text-red-800">Ignore all &amp; overwrite (destructive)</div>
                <div className="text-xs text-slate-600">
                  Wipes the facilities collection and inserts only rows from the file. All existing facilities not in the file
                  are removed. Current statuses reset to ACTIVE.
                </div>
              </div>
            </label>

            {replaceMode === "cross_reference" && (
              <label className="mt-3 flex items-start gap-2 text-xs text-slate-700">
                <input
                  data-testid={TID.massReplaceDeleteMissing}
                  type="checkbox"
                  className="mt-0.5"
                  checked={deleteMissing}
                  onChange={(e) => setDeleteMissing(e.target.checked)}
                />
                <span>
                  <b>Hard-delete</b> facilities that are missing from the new file
                  ({replacePreview.counts.removes} rows). If unchecked, they will only be flagged
                  as <code>missing_from_last_import</code>.
                </span>
              </label>
            )}
          </div>

          <div className="flex justify-end">
            <button
              data-testid="mass-replace-open-confirm"
              onClick={() => setShowConfirm(true)}
              className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700"
            >
              <Trash2 size={14} strokeWidth={2.5} />
              {replaceMode === "overwrite" ? "Wipe & replace" : "Apply changes"}
            </button>
          </div>
        </div>
      )}

      {replaceReport && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-900">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={16} className="text-emerald-700" />
            <div className="font-heading text-sm font-bold">Mass replacement complete ({replaceReport.mode})</div>
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <div><b>{replaceReport.added}</b> added</div>
            <div><b>{replaceReport.updated}</b> updated</div>
            <div><b>{replaceReport.removed_flagged}</b> flagged missing</div>
            <div><b>{replaceReport.removed_deleted}</b> deleted</div>
          </div>
          {replaceReport.backup_collection && (
            <div className="mt-2">
              Backup snapshot saved: <code className="font-mono">{replaceReport.backup_collection}</code>
              {" "}({replaceReport.backup_row_count} rows). Restore via Mongo shell if needed.
            </div>
          )}
        </div>
      )}

      {showConfirm && (
        <MassReplaceConfirmModal
          mode={replaceMode}
          preview={replacePreview}
          deleteMissing={deleteMissing}
          onCancel={() => setShowConfirm(false)}
          onExecute={executeReplace}
        />
      )}
    </div>
  );
};
